// tabs/ThinkyBaaSGovernance.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGavel, faCheckCircle, faFileAlt, faPlay, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

// Dummy Governance & Compliance data
const dummyPolicies = [
  {
    id: 1,
    name: "Financial Transaction Policy",
    description: "Ensure all transactions follow KYC & AML regulations.",
    defined: false,
    audited: false,
    approved: false,
    log: [],
    precondition: true,
  },
  {
    id: 2,
    name: "Supply Chain Compliance Policy",
    description: "Monitor supplier compliance and certifications.",
    defined: false,
    audited: false,
    approved: false,
    log: [],
    precondition: false, // simulate missing supplier data
  },
  {
    id: 3,
    name: "Data Privacy Policy",
    description: "Ensure GDPR and HIPAA compliance for user data.",
    defined: false,
    audited: false,
    approved: false,
    log: [],
    precondition: true,
  },
];

const ThinkyBaaSGovernance = () => {
  const [policies, setPolicies] = useState(dummyPolicies.map(p => ({ ...p, log: p.log || [] })));

  // Define Policy
  const definePolicy = (id) => {
    setPolicies(prev =>
      prev.map(p =>
        p.id === id && !p.defined
          ? { ...p, defined: true, log: [...(p.log || []), { event: "Policy Defined", time: new Date().toLocaleTimeString() }] }
          : p
      )
    );
  };

  // Audit Policy
  const auditPolicy = (id) => {
    setPolicies(prev =>
      prev.map(p => {
        if (p.id === id) {
          if (!p.precondition) {
            return { ...p, log: [...(p.log || []), { event: "Audit blocked: precondition not met", time: new Date().toLocaleTimeString() }] };
          }
          return { ...p, audited: true, log: [...(p.log || []), { event: "Policy Audited", time: new Date().toLocaleTimeString() }] };
        }
        return p;
      })
    );
  };

  // Approve Policy
  const approvePolicy = (id) => {
    setPolicies(prev =>
      prev.map(p =>
        p.id === id && p.audited && !p.approved
          ? { ...p, approved: true, log: [...(p.log || []), { event: "Policy Approved ✅", time: new Date().toLocaleTimeString() }] }
          : p
      )
    );
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl shadow-lg space-y-6">
      <div className="flex items-center mb-4">
        <FontAwesomeIcon icon={faGavel} className="text-blue-400 text-lg lg:text-2xl mr-4" />
        <h2 className="text-lg lg:text-xl font-bold">Governance & Compliance Workflow</h2>
      </div>

      <p className="text-gray-300 text-sm">
        Simulated <span className="font-semibold">Governance & Compliance workflow</span>:
        Define → Audit → Approve. Event logs track each step to ensure accountability.
      </p>

      <div className="space-y-4">
        {policies.map(p => (
          <div key={p.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <h3 className="text-xl font-semibold mb-1">{p.name}</h3>
            <p className="text-gray-400 mb-2">{p.description}</p>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`px-2 py-1 rounded-sm text-sm ${p.defined ? "bg-green-600" : "bg-yellow-600"}`}>
                {p.defined ? "Defined ✅" : "Pending Definition ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${p.audited ? "bg-green-600" : "bg-yellow-600"}`}>
                {p.audited ? "Audited ✅" : "Pending Audit ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${p.approved ? "bg-green-600" : "bg-yellow-600"}`}>
                {p.approved ? "Approved ✅" : "Pending Approval ⏳"}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                disabled={p.defined}
                onClick={() => definePolicy(p.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  p.defined ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faFileAlt} className="mr-1" /> Define
              </button>
              <button
                disabled={!p.defined || p.audited}
                onClick={() => auditPolicy(p.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !p.defined || p.audited ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-1" /> Audit
              </button>
              <button
                disabled={!p.audited || p.approved}
                onClick={() => approvePolicy(p.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !p.audited || p.approved ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Approve
              </button>
            </div>

            <div className="mt-3 bg-gray-900 p-2 rounded-lg border border-gray-700">
              <h4 className="text-gray-300 font-semibold mb-1">Event Log:</h4>
              {(!p.log || p.log.length === 0) ? (
                <p className="text-gray-500 text-sm">No events yet.</p>
              ) : (
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {p.log?.map((log, idx) => (
                    <li key={idx}>
                      [{log.time}] {log.event}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThinkyBaaSGovernance;
